/**
 * MM Admin — supplier commission statements.
 * Live ledger for any dates, plus numbered statements an admin can email.
 * This page does not post a journal and does not issue a tax invoice.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Download, Mail, FilePlus } from 'lucide-react';

const API_BASE = '/api/v1/admin/supplier-statements';

interface SupplierRow {
  supplier: string;
  account: string;
  postedCents: number;
  feeCents?: number;
  documentType: string;
  feeNote?: string;
  status: string;
  asksForPayment: boolean;
  canIssue?: boolean;
}

interface IssuedStatement {
  serial: string;
  supplier: string;
  account: string;
  periodFrom: string;
  periodTo: string;
  postedCents: number;
  documentNote: string;
  paymentRequest: boolean;
  issuedAt: string;
  emailedTo: string | null;
  emailStatus: string;
}

interface StatementData {
  from: string;
  to: string;
  entity: { name: string; registration: string; vatNumber?: string };
  suppliers: SupplierRow[];
  issued: IssuedStatement[];
  vatNotice: string;
}

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('portal_token');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function sastToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

function shiftMonth(iso: string, delta: number): { from: string; to: string } {
  const [year, month] = iso.slice(0, 7).split('-').map(Number);
  const start = new Date(Date.UTC(year, month - 1 + delta, 1));
  const end = new Date(Date.UTC(year, month + delta, 0));
  const fmt = (date: Date) => date.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

function rands(cents: number): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);
}

function statusLabel(row: SupplierRow): string {
  if (row.status === 'no activity') return 'No activity';
  if (row.asksForPayment) return 'Payable by supplier';
  if (row.status === 'legacy_posted') return 'Legacy fee';
  return 'Retained in float';
}

export const SupplierStatementsOverlay: React.FC = () => {
  const today = sastToday();
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<StatementData | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyAccount, setBusyAccount] = useState('');
  const [emails, setEmails] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ from, to });
      const response = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) {
        setError('Supplier statements could not be loaded.');
        setData(null);
        return;
      }
      const json = await response.json();
      setData(json.data as StatementData);
    } catch {
      setError('Supplier statements could not be loaded.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (account: string) => {
    setBusyAccount(account);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_BASE}/issue`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, from, to }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(json.message || 'The statement could not be issued.');
        return;
      }
      setNotice(`${json.data.serial} is saved for ${json.data.supplier}. Enter the supplier email on that row to send it.`);
      await load();
    } catch {
      setError('The statement could not be issued.');
    } finally {
      setBusyAccount('');
    }
  };

  const downloadIssued = async (serial: string) => {
    const response = await fetch(`${API_BASE}/issued/${encodeURIComponent(serial)}/download`, { headers: authHeaders() });
    if (!response.ok) {
      setError('The statement could not be downloaded.');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${serial}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const send = async (serial: string) => {
    const email = (emails[serial] || '').trim();
    setError('');
    setNotice('');
    const response = await fetch(`${API_BASE}/issued/${encodeURIComponent(serial)}/send`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(json.message || 'The statement could not be sent.');
      return;
    }
    setNotice(json.message || 'Sent.');
    await load();
  };

  const suppliers = data?.suppliers || [];
  const issued = data?.issued || [];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--foreground)]">Supplier statements</h1>
        <p className="mt-2 max-w-3xl text-sm text-[var(--foreground)]">
          Each row is one supplier. Commission is what MyMoolah earned. Where the supplier also charges a rail fee, that fee is shown on the same row. It is the supplier fee already taken from the float, not MyMoolah's markup.
          Issue a statement when you need a numbered copy. You can email that copy from the history below.
        </p>
        <p className="mt-2 max-w-3xl text-sm text-[var(--muted-foreground)]">
          {data?.vatNotice || 'VAT number: TBA. This is a supplier statement, not a SARS tax invoice.'}
          {' '}Channel-partner tax invoices stay on Partner settlement. They are not listed here.
        </p>
      </div>

      <section className="mymoolah-card flex flex-wrap items-end gap-3 p-4">
        <label className="text-sm text-[var(--muted-foreground)]" htmlFor="supplier-from">From</label>
        <input id="supplier-from" type="date" className="rounded-md border border-[var(--border)] bg-white px-3 py-2 text-sm" value={from} onChange={(event) => setFrom(event.target.value)} />
        <label className="text-sm text-[var(--muted-foreground)]" htmlFor="supplier-to">To</label>
        <input id="supplier-to" type="date" className="rounded-md border border-[var(--border)] bg-white px-3 py-2 text-sm" value={to} onChange={(event) => setTo(event.target.value)} />
        <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => { setFrom(monthStart(sastToday())); setTo(sastToday()); }}>This month to date</button>
        <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => { const last = shiftMonth(sastToday(), -1); setFrom(last.from); setTo(last.to); }}>Last month</button>
      </section>

      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      {notice ? <p className="text-sm text-[var(--foreground)]">{notice}</p> : null}
      {loading ? <p className="text-sm text-[var(--muted-foreground)]">Loading statements…</p> : null}

      <section className="mymoolah-card p-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">Posted commission</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          {data?.entity ? `${data.entity.name}, ${data.entity.registration}` : 'MyMoolah (Pty) Ltd'}
          {data?.entity?.vatNumber ? `, VAT ${data.entity.vatNumber}` : ''}
          {data ? ` · ${data.from} to ${data.to}` : ''}
        </p>
        <table className="mt-4 w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="py-2 pr-3">Supplier</th>
              <th className="py-2 pr-3">Posted</th>
              <th className="py-2 pr-3">Meaning</th>
              <th className="py-2">Statement</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((row) => (
              <tr key={row.account} className="border-b border-[var(--border)] align-top">
                <td className="py-3 pr-3">
                  <div className="font-medium">{row.supplier}</div>
                  <div className="text-xs text-[var(--muted-foreground)]">{row.account}</div>
                </td>
                <td className="py-3 pr-3">
                  <div>{rands(row.postedCents)}</div>
                  {row.feeCents ? <div className="mt-1 text-xs text-[var(--muted-foreground)]">Rail fee {rands(row.feeCents)}</div> : null}
                </td>
                <td className="py-3 pr-3">
                  <div>{statusLabel(row)}</div>
                  <div className="mt-1 max-w-md text-xs text-[var(--muted-foreground)]">{row.documentType}</div>
                  {row.feeNote ? <div className="mt-1 max-w-md text-xs text-[var(--muted-foreground)]">{row.feeNote}</div> : null}
                </td>
                <td className="py-3">
                  <button type="button" className="rounded-md border px-3 py-2 text-sm" disabled={busyAccount === row.account || row.canIssue === false} onClick={() => issue(row.account)}>
                    <FilePlus className="mr-1 inline h-4 w-4" />
                    {row.canIssue === false ? 'Not a supplier' : busyAccount === row.account ? 'Issuing…' : 'Issue statement'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mymoolah-card p-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">Issued statements</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          These are the copies an admin has issued. Earlier months were not stored, so this list starts with the first statement you issue.
        </p>
        {issued.length === 0 && !loading ? (
          <p className="mt-4 text-sm">No statement has been issued yet.</p>
        ) : (
          <table className="mt-4 w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="py-2 pr-3">Statement</th>
                <th className="py-2 pr-3">Supplier</th>
                <th className="py-2 pr-3">Dates</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2">Send</th>
              </tr>
            </thead>
            <tbody>
              {issued.map((row) => (
                <tr key={row.serial} className="border-b border-[var(--border)] align-top">
                  <td className="py-3 pr-3">
                    <div className="font-medium">{row.serial}</div>
                    <button type="button" className="mt-1 text-xs underline" onClick={() => downloadIssued(row.serial)}>
                      <Download className="mr-1 inline h-3 w-3" /> PDF
                    </button>
                  </td>
                  <td className="py-3 pr-3">{row.supplier}</td>
                  <td className="py-3 pr-3">{row.periodFrom} to {row.periodTo}</td>
                  <td className="py-3 pr-3">{rands(Number(row.postedCents))}</td>
                  <td className="py-3">
                    <div className="text-xs text-[var(--muted-foreground)]">
                      {row.emailStatus === 'sent' ? `Sent to ${row.emailedTo}` : 'Not sent'}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <input
                        type="email"
                        aria-label={`Email for ${row.serial}`}
                        className="rounded-md border border-[var(--border)] bg-white px-3 py-2 text-sm"
                        placeholder="supplier@example.com"
                        value={emails[row.serial] ?? row.emailedTo ?? ''}
                        onChange={(event) => setEmails((current) => ({ ...current, [row.serial]: event.target.value }))}
                      />
                      <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => send(row.serial)}>
                        <Mail className="mr-1 inline h-4 w-4" /> Send
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
